import { Select } from '@civilgrid/ui/components';
import Dinero from 'dinero.js';
import { useEffect, useState } from 'react';
import type { AsyncState } from 'react-use/lib/useAsyncFn';
import { useSnapshot } from 'valtio';
import type { GetCostOutput } from '~/common/billingUtils';
import rootStore from '~/store';
import { visibleCostBreakdownTiers } from './costBreakdownTiers';

interface CostBreakdownProps {
  projectCost: AsyncState<GetCostOutput>;
}

export default function CostBreakdown({ projectCost }: CostBreakdownProps) {
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const items = visibleCostBreakdownTiers(currentOrganization?.subscription);
  const [selectedBillingTier, setSelectedBillingTier] = useState<string>(
    () => (items.some((i) => i.value === 'project') ? 'project' : items[0]?.value) ?? 'project',
  );

  // The initializer runs once, before the org subscription loads (when all tiers are visible). If the
  // org later disables the selected tier, it drops out of `items` — re-point the selection to a valid
  // tier so the Select and cost table never reference a hidden tier.
  useEffect(() => {
    if (items.length > 0 && !items.some((i) => i.value === selectedBillingTier)) {
      const fallback = items.some((i) => i.value === 'project') ? 'project' : items[0].value;
      setSelectedBillingTier(fallback);
    }
  }, [items, selectedBillingTier]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="">
      <div className="flex justify-end">
        <Select
          aria-label="Billing tier"
          options={items}
          value={selectedBillingTier}
          onValueChange={(value) => value && setSelectedBillingTier(value)}
          triggerClassName="w-fit min-w-[8rem]"
          data-testid="cost-breakdown-billing-tier-select"
        />
      </div>

      <div className="mt-2">
        <table className="w-full text-left text-base">
          <thead>
            <tr className="bg-gray-100">
              <th></th>
              <th className="py-2">Cost</th>
              <th>Qty</th>
              <th className="pr-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {projectCost.value?.[selectedBillingTier as keyof GetCostOutput].totalExplanation.map((c, i) => (
              <tr
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={`cost-explanation-${i}`}
                className="border-b"
              >
                <td className="py-2">{c.dimension}</td>
                <td>{c.unitCost}</td>
                <td>{c.quantity}</td>
                <td className="pr-2 text-right">{c.total}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="pt-2 font-bold">Total</td>
              <td></td>
              <td></td>
              <td className="pr-2 text-right font-bold">
                {Dinero({
                  amount: projectCost.value?.[selectedBillingTier as keyof GetCostOutput].totalCost,
                }).toFormat()}
              </td>
            </tr>
          </tfoot>
        </table>
        <div className="mt-2 text-xs text-secondary">
          <span className="font-bold">*</span> This does not include credit for existing spend.
        </div>
      </div>
    </div>
  );
}
